import type { FacilityAddress } from '../index.js'
import { findRegion, isKnownRegion, regionLabelOf } from '../../core/index.js'

/** The address form's fields, all as strings so an empty one is '' rather than absent. */
export interface AddressDraft {
  line1: string
  line2: string
  city: string
  region: string
  postalCode: string
  country: string
}

export const DEFAULT_COUNTRY = 'US'
export const EMPTY_ADDRESS: AddressDraft = { line1: '', line2: '', city: '', region: '', postalCode: '', country: DEFAULT_COUNTRY }

/** The draft of a saved address; a region saved as a name or in lower case ("New York", "ny") reads as its code, as the field now stores it. */
export function draftFromAddress(address: FacilityAddress | undefined): AddressDraft {
  if (!address) {
    return EMPTY_ADDRESS
  }
  const region = findRegion(address.country, address.region)?.code ?? address.region
  return { ...EMPTY_ADDRESS, ...address, line2: address.line2 ?? '', region }
}

/** True once the person has typed anything beyond the default country. */
export function hasAddressContent(draft: AddressDraft): boolean {
  return [draft.line1, draft.line2, draft.city, draft.region, draft.postalCode].some((field) => field.trim() !== '')
}

/** What is still missing before the address can be saved, in the order the form shows the fields. */
export function missingAddressFields(draft: AddressDraft): string[] {
  const regionLabel = regionLabelOf(draft.country).toLowerCase()
  const required: [keyof AddressDraft, string][] = [
    ['line1', 'street'],
    ['city', 'city'],
    ['region', regionLabel],
    ['postalCode', 'postal code'],
  ]
  const missing = required.filter(([key]) => draft[key].trim() === '').map(([, label]) => label)
  const unknownRegion = draft.region.trim() !== '' && !isKnownRegion(draft.country, draft.region) ? [`a ${regionLabel} from the list`] : []
  const withRegion = [...missing, ...unknownRegion]
  return /^[A-Za-z]{2}$/.test(draft.country.trim()) ? withRegion : [...withRegion, 'a two-letter country code']
}

/** The draft with every field trimmed and the country upper-cased, as it would read back after a save. */
export function normalisedDraft(draft: AddressDraft): AddressDraft {
  return {
    line1: draft.line1.trim(),
    line2: draft.line2.trim(),
    city: draft.city.trim(),
    region: draft.region.trim(),
    postalCode: draft.postalCode.trim(),
    country: draft.country.trim().toUpperCase(),
  }
}

/** The draft as the API takes it: trimmed, line2 left out when empty, the country upper-cased. */
export function addressFromDraft(draft: AddressDraft): FacilityAddress {
  const line2 = draft.line2.trim()
  return {
    line1: draft.line1.trim(),
    ...(line2 === '' ? {} : { line2 }),
    city: draft.city.trim(),
    region: draft.region.trim(),
    postalCode: draft.postalCode.trim(),
    country: draft.country.trim().toUpperCase(),
  }
}

/** The address and phone as one line for a header or a row - only what is set, nothing when neither is. */
export function formatContact(facility: { address?: FacilityAddress; phone?: string }): string | null {
  const parts = [facility.address ? formatAddress(facility.address) : null, facility.phone ?? null].filter((part): part is string => part !== null && part !== '')
  return parts.length === 0 ? null : parts.join(' · ')
}

export function formatAddress(address: FacilityAddress): string {
  return [address.line1, address.line2, `${address.city}, ${address.region} ${address.postalCode}`, address.country].filter((part) => part && part.trim() !== '').join(', ')
}

/** Whether an address is worth asking the API to place: a US postal code with its five digits typed. */
export function canSuggestTimezone(draft: AddressDraft): boolean {
  return draft.country.trim().toUpperCase() === DEFAULT_COUNTRY && /^\d{5}/.test(draft.postalCode.trim())
}
